import React, { useState, useRef, useEffect } from 'react';
import { Send, Trash2, Download } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Anthropic from '@anthropic-ai/sdk';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks'
import { getProjectData, saveProjectData, getGlobalData } from '../utils/projectStorage';
import './Testing.css';
import '../styles/markdown.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const Testing: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  const [showIngestionResponse, setShowIngestionResponse] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Extract project ID from URL
  const getProjectId = (): string => {
    const pathParts = location.pathname.split('/');
    const projectIndex = pathParts.indexOf('project');
    if (projectIndex !== -1 && projectIndex + 1 < pathParts.length) {
      return pathParts[projectIndex + 1];
    }
    return 'sales-assistant'; // fallback
  };

  const projectId = getProjectId();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Load message history from project-specific storage
    const projectData = getProjectData(projectId);
    if (projectData.testingHistory) {
      setMessages(projectData.testingHistory);
    }
  }, [projectId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Save message history to project-specific storage whenever it changes
    if (messages.length > 0) {
      const projectData = getProjectData(projectId);
      projectData.testingHistory = messages;
      saveProjectData(projectId, projectData);
    }
  }, [messages, projectId]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const globalData = getGlobalData();
    if (!globalData.apiKey) {
      alert('Please configure your Anthropic API key in Settings');
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const anthropic = new Anthropic({
        apiKey: globalData.apiKey,
        dangerouslyAllowBrowser: true
      });

      // Get ingested content from project-specific storage
      const projectData = getProjectData(projectId);
      const ingestedContent = projectData.ingestedContent;
      
      // Build conversation with context
      const systemPrompt = ingestedContent || 'You are a helpful assistant.';
      const fileIds = projectData.fileIds || [];

      let files: any[] = [];
      // Build message content with file attachments
      const messageContent: any[] = [
        {
          type: 'text',
          text: inputMessage
        }
      ];

      // Add file references
      fileIds.forEach((fileId: string) => {
        files.push({
          type: 'document',
          source: {
            type: 'file',
            file_id: fileId
          }
        });
      });

      messageContent.push(...files);
      console.log(messageContent);

      const response = await anthropic.beta.messages.create({
        model: 'claude-sonnet-4-20250514',
        betas: ["files-api-2025-04-14"],
        max_tokens: 4096,
        system: [
          {
            type: 'text',
            text: systemPrompt,
            cache_control: { type: 'ephemeral' }
          }
        ],
        messages: [
          {
            role: 'user',
            content: [
                {
                    type: 'text',
                    text: "Use the ingested content to answer the user's question. Review attached files and reference them if needed.",
                }
                ]
          },
          ...messages.map(msg => ({
            role: msg.role,
            content: msg.content
          })),
          {
            role: 'user',
            content: messageContent
          }
        ]
      });

      console.log('Anthropic response:', response);

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.content[0].type === 'text' ? response.content[0].text : '',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please check your API key and try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const clearConversation = () => {
    if (window.confirm('Are you sure you want to clear the conversation?')) {
      setMessages([]);
      const projectData = getProjectData(projectId);
      projectData.testingHistory = [];
      saveProjectData(projectId, projectData);
    }
  };

  const exportConversation = () => {
    const conversationText = messages
      .map(msg => `${msg.role.toUpperCase()} (${new Date(msg.timestamp).toLocaleString()}):\n${msg.content}\n`)
      .join('\n---\n\n');

    const blob = new Blob([conversationText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectId}-testing-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="testing-page">
      <div className="testing-header">
        <h2>Testing</h2>
        <div className="testing-actions">
          <button onClick={clearConversation} className="action-btn">
            <Trash2 size={16} /> Clear
          </button>
          <button onClick={exportConversation} className="action-btn">
            <Download size={16} /> Export
          </button>
        </div>
      </div>
      <div className="system-prompt-container">
        <div className="system-prompt">
          <h3 className="mb-0" onClick={() => setShowSystemPrompt(!showSystemPrompt)} style={{ cursor: 'pointer' }}>
            {showSystemPrompt ? '▼' : '▶'} System Prompt
          </h3>
          {showSystemPrompt && (
            <textarea className="w-full h-32 p-2" readOnly value={getProjectData(projectId).ingestedContent || ''}>
            </textarea>
          )}
        </div>
      </div>

      {getProjectData(projectId)?.ingestionResponse &&
          <div className="system-prompt-container">
            <div className="system-prompt">
              <h3 onClick={() => setShowIngestionResponse(!showIngestionResponse)} style={{ cursor: 'pointer' }}>
                {showIngestionResponse ? '▼' : '▶'} Settings Ingestion Response
              </h3>
              {showIngestionResponse && (
                <textarea className="w-full h-32 p-2" readOnly value={JSON.stringify(getProjectData(projectId).ingestionResponse, null, "\t") || ''}>
                </textarea>
              )}
            </div>
          </div>
      }
      <div className="chat-container">
        <div className="messages-container">
          {messages.length === 0 && (
            <div className="empty-state">
              <p>Start a conversation to test your AI assistant</p>
              <p className="hint">Messages will use your ingested content if available</p>
            </div>
          )}
          
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.role}`}>
              <div className="message-header">
                <span className="message-role">
                  {message.role === 'user' ? 'You' : 'Assistant'}
                </span>
                <span className="message-time">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </span>
              </div>
              <div className="message-content">
                {message.role === 'assistant' ? (
                  <div className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {message.content.replace(/\n+/g, "\n")}
                    </ReactMarkdown>
                  </div>
                ) : (
                  message.content
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="message assistant loading">
              <div className="message-header">
                <span className="message-role">Assistant</span>
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="message-input"
            rows={1}
            disabled={loading}
          />
          <button 
            onClick={sendMessage} 
            className="send-btn"
            disabled={!inputMessage.trim() || loading}
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Testing;